// src/pages/Enseignants/ListingAdherents/AdherentCard.jsx
import React, { useState, useCallback, useEffect } from "react";
import {
  FaBirthdayCake,
  FaEdit,
  FaPlus,
  FaEllipsisV,
  FaTimes,
} from "react-icons/fa";
import { MdMedicalServices } from "react-icons/md";
import { statusColor } from "./utils";
import { toast } from "react-toastify";
import { updateBlessures } from "../../../services/adherentServices";

import ReglementStatusBadge from "../../../components/Adherents/ReglementStatusBadge";

const AdherentCard = ({
  adherent,
  isBirthday,
  getDefaultPhoto,
  onClick,
  isAdmin,
  onEdit,
  onManageGroups,
  refreshData,
}) => {
  // ---------------- UI state ----------------
  const [adminOpen, setAdminOpen] = useState(false);

  // Quick bless popover (ancré dans la carte)
  const [showBlessModal, setShowBlessModal] = useState(false);
  const [localIsBlessed, setLocalIsBlessed] = useState(!!adherent?.is_blessed);
  const [localDateReprise, setLocalDateReprise] = useState(
    adherent?.date_reprise || "",
  );
  const [localNotesAdherent, setLocalNotesAdherent] = useState(
    adherent?.notes_adherent || "",
  );
  const [saving, setSaving] = useState(false);

  // ---------------- Helpers styles (Tailwind only) ----------------
  const btnBase =
    "inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition active:scale-[.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
  const btnPrimary =
    "text-white ring-1 ring-inset ring-blue-600/20 bg-gradient-to-r from-blue-600 to-fuchsia-600 hover:opacity-95 focus-visible:ring-blue-400/60";
  const btnGhostLight =
    "text-white ring-1 ring-inset ring-white/25 bg-white/10 hover:bg-white/20";
  const btnSubtle =
    "text-slate-700 dark:text-slate-100 ring-1 ring-inset ring-slate-300/70 dark:ring-slate-600/70 bg-white dark:bg-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-700/80";

  const inputBase =
    "w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50";
  const labelBase =
    "block text-xs font-medium mb-1 text-slate-600 dark:text-slate-300";

  // ---------------- Handlers ----------------
  const handleCardClick = useCallback(() => {
    onClick?.(adherent);
  }, [adherent, onClick]);

  const toggleAdmin = useCallback((e) => {
    e.stopPropagation();
    setAdminOpen((v) => !v);
  }, []);

  const openQuickBless = useCallback(
    (e) => {
      e.stopPropagation();
      // sync avec valeurs actuelles
      setLocalIsBlessed(!!adherent?.is_blessed);
      setLocalDateReprise(adherent?.date_reprise || "");
      setLocalNotesAdherent(adherent?.notes_adherent || "");
      setShowBlessModal(true);
    },
    [adherent],
  );

  const closeQuickBless = useCallback(() => {
    setShowBlessModal(false);
  }, []);

  // Fermeture avec Escape quand le popover est ouvert
  useEffect(() => {
    if (!showBlessModal) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowBlessModal(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showBlessModal]);

  const handleSaveQuickBless = useCallback(async () => {
    try {
      setSaving(true);
      await updateBlessures(adherent.id, {
        is_blessed: !!localIsBlessed,
        date_reprise: localIsBlessed ? localDateReprise || null : null,
        notes_adherent: localNotesAdherent || null,
      });
      toast.success("Absence / blessure mise à jour.");
      setShowBlessModal(false);
      if (typeof refreshData === "function") await refreshData();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la mise à jour.");
    } finally {
      setSaving(false);
    }
  }, [
    adherent?.id,
    localIsBlessed,
    localDateReprise,
    localNotesAdherent,
    refreshData,
  ]);

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    if (
      /^https?:\/\//i.test(s) ||
      s.startsWith("data:") ||
      s.startsWith("blob:")
    )
      return s;
    if (!s.startsWith("/uploads")) return s;
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  const photoSrc = adherent?.autorisation_photo
    ? toAbsolute(adherent?.photo_url) || getDefaultPhoto(adherent?.genre)
    : "/img/interdiction.jpg";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => e.key === "Enter" && handleCardClick()}
      className="relative group rounded-xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white dark:bg-slate-900 cursor-pointer"
    >
      {/* Badges */}
      {isBirthday && (
        <div className="absolute top-2 right-2 z-10">
          <FaBirthdayCake className="text-white bg-primary rounded-full p-2 w-10 h-10 shadow-lg" />
        </div>
      )}

      {adherent?.is_blessed && (
        <button
          type="button"
          onClick={openQuickBless}
          title="Absence / blessure (cliquer pour modifier)"
          className="absolute top-2 left-2 z-10 rounded-full outline-none focus:ring-2 focus:ring-white/70"
        >
          <MdMedicalServices className="text-white bg-red-600 rounded-full p-2 w-10 h-10 shadow-lg hover:scale-105 active:scale-95 transition" />
        </button>
      )}

      {/* Bouton mobile pour actions admin */}
      {isAdmin && (
        <button
          type="button"
          onClick={toggleAdmin}
          title={adminOpen ? "Fermer les actions" : "Ouvrir les actions"}
          className="md:hidden absolute bottom-2 right-2 z-10 rounded-full bg-slate-900/85 text-white w-10 h-10 flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition"
        >
          <FaEllipsisV />
        </button>
      )}

      {/* Photo */}
      <figure className="aspect-square">
        <img
          src={photoSrc}
          alt={`${adherent?.nom || ""} ${adherent?.prenom || ""}`}
          className="object-cover w-full h-full"
          loading="lazy"
        />
      </figure>

      {/* Informations adhérent + règlement */}
      <div className="space-y-2.5 p-3">
        {/* Nom */}
        <div className="text-center">
          <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
            {(adherent?.nom || "").toUpperCase()}
          </p>

          <p className="truncate text-sm font-medium text-slate-600 dark:text-slate-300">
            {adherent?.prenom || "—"}
          </p>
        </div>

        {/* Règlement inscription */}
        <div>
          <p className="mb-1 text-center text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Règlement inscription
          </p>

          <ReglementStatusBadge statut={adherent?.statut_reglement} />
        </div>

        {/* Statut inscription */}
        <div className="flex justify-center">
          <span
            className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ${statusColor(
              adherent?.statut_inscription,
            )}`}
          >
            {adherent?.statut_inscription || "Sans statut"}
          </span>
        </div>
      </div>

      {/* Barre d'actions admin (hover desktop / toggle mobile) */}
      {isAdmin && (
        <div
          onClick={(e) => e.stopPropagation()}
          className={[
            "absolute inset-x-0 bottom-0",
            "bg-gradient-to-r from-blue-500/95 via-violet-500/95 to-pink-500/95",
            "backdrop-blur-sm text-white",
            "px-3 py-2 md:py-3",
            "flex items-center justify-center gap-3",
            "shadow-[0_-10px_22px_-12px_rgba(0,0,0,0.45)]",
            "transition-transform duration-250",
            "min-h-[72px]",
            adminOpen
              ? "translate-y-0"
              : "translate-y-full md:group-hover:translate-y-0",
          ].join(" ")}
        >
          {/* poignée mobile */}
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-12 h-1.5 rounded-full bg-white/70 md:hidden" />

          <button
            className={`${btnBase} ${btnGhostLight} px-2.5 py-1.5 text-xs`}
            onClick={() => onEdit?.(adherent)}
          >
            <FaEdit className="mr-1" />{" "}
            <span className="hidden sm:inline">Éditer</span>
          </button>

          <button
            className={`${btnBase} ${btnGhostLight} px-2.5 py-1.5 text-xs`}
            onClick={() => onManageGroups?.(adherent)}
          >
            <FaPlus className="mr-1" />{" "}
            <span className="hidden sm:inline">Gérer groupes</span>
          </button>
        </div>
      )}

      {/* ----- Quick Bless Popover (ANCRÉ DANS LA CARTE) ----- */}
      {showBlessModal && (
        <div
          className="absolute inset-0 z-20"
          onClick={(e) => {
            // clic dans la carte mais hors popover -> ferme
            e.stopPropagation();
            closeQuickBless();
          }}
        >
          {/* léger scrim limité à la carte */}
          <div className="absolute inset-0 bg-slate-900/10" />

          {/* Panneau ancré près de l'icône (top-left) */}
          <div
            role="dialog"
            aria-modal="true"
            className="absolute top-2 left-2 w-[94%] max-w-[22rem] rounded-xl shadow-xl ring-1 ring-slate-200/80 dark:ring-slate-700/70 bg-white/95 dark:bg-slate-900/95 backdrop-blur p-3 text-[13px] leading-5"
            onClick={(e) => e.stopPropagation()} // ne pas fermer si on clique dedans
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <MdMedicalServices className="text-red-600 w-5 h-5" />
                <h3 className="text-sm font-semibold">Absence / Blessure</h3>
              </div>
              <button
                type="button"
                onClick={closeQuickBless}
                className="rounded-md p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
                aria-label="Fermer"
              >
                <FaTimes />
              </button>
            </div>

            <label className="flex items-center gap-2 mb-3 select-none">
              <input
                type="checkbox"
                className="h-4 w-4 accent-blue-600 rounded"
                checked={localIsBlessed}
                onChange={(e) => setLocalIsBlessed(e.target.checked)}
              />
              <span className="text-[13px]">
                Judoka(te) malade ou blessé(e)
              </span>
            </label>

            <div className="mb-3">
              <label className={labelBase}>Date de reprise</label>
              <input
                type="date"
                className={inputBase}
                value={localIsBlessed ? localDateReprise || "" : ""}
                onChange={(e) => setLocalDateReprise(e.target.value)}
                disabled={!localIsBlessed}
              />
            </div>

            <div className="mb-3">
              <label className={labelBase}>
                Note (description de l’absence)
              </label>
              <textarea
                className={`${inputBase} min-h-[72px] resize-y`}
                placeholder="Ex : entorse cheville droite, repos 10 jours…"
                value={localNotesAdherent}
                onChange={(e) => setLocalNotesAdherent(e.target.value)}
                disabled={!localIsBlessed}
              />
            </div>

            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                className={`${btnBase} ${btnSubtle} px-3 py-1.5`}
                onClick={closeQuickBless}
                disabled={saving}
              >
                Annuler
              </button>
              <button
                type="button"
                className={`${btnBase} ${btnPrimary} px-3 py-1.5`}
                onClick={handleSaveQuickBless}
                disabled={saving}
              >
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>

            {/* petit triangle / caret décoratif */}
            <div className="absolute -top-1 left-6 w-3 h-3 rotate-45 bg-white dark:bg-slate-900 ring-1 ring-slate-200/80 dark:ring-slate-700/70" />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdherentCard;
